import Model from 'core/model/Model';
import { computed } from 'mobx';
import moment from 'moment';
import { uniqWith, isEqual } from 'lodash';
import $notifications from 'app/stores/notifications/$notifications';
import $labels from 'app/stores/label/$labels';
import transformConfigToFilters from 'app/util/mkp/transformConfigToFilters';

import FlowPolicyModel from 'app/stores/alerting/FlowPolicyModel';
import UserCollection from '../user/UserCollection';

// TODO NMS POLICY - if we ever get subpolicies, this will need to be updated
export default class TenantModel extends Model {
  get urlRoot() {
    return '/api/ui/mkp-settings/tenant';
  }

  get defaults() {
    return {
      type: 'subtenant',
      enabled: true
    };
  }

  // Only show tenant users
  users = new UserCollection([], {
    activePresetFilter: {
      fn: (model) => model.get('user_group_id')
    }
  });

  deserialize(data) {
    if (!data) {
      return data;
    }

    const { config, users, template, subpolicies } = data;

    if (users && Array.isArray(users)) {
      this.users.set(users);
    }

    data.template_id = undefined;
    data.template = [];
    data.subpolicies = Array.isArray(subpolicies) ? subpolicies : [];

    if (template && template.length) {
      const [activeTemplate] = template;
      data.template = activeTemplate;
      data.template_id = activeTemplate.id;
    }

    data.subpolicies = data.subpolicies.map((subpolicy) =>
      new FlowPolicyModel().deserialize({
        ...subpolicy,
        thresholds: $notifications.addNotificationsToThresholds(subpolicy?.thresholds || [], subpolicy)
      })
    );

    return super.deserialize({ ...data, config });
  }

  serialize(data) {
    const { config, subpolicies } = data;

    const serializedPolicies = subpolicies
      .filter((policy) => !policy.applicationMetadata?.isFromTemplate)
      .map((policy) => {
        const serializedPolicy = new FlowPolicyModel().serialize(policy);
        const serializedThresholds = serializedPolicy.thresholds.map((threshold) => ({
          ...threshold,
          mitigations: threshold.mitigations.map(({ id, ...mitigation }) => ({
            ...mitigation,
            applicationMetadata: {
              type: 'subtenant',
              tenantID: `${this.id}`
            }
          }))
        }));
        return { ...serializedPolicy, id: policy.id, thresholds: serializedThresholds };
      });

    const newConfig = {
      ...config,
      filters: transformConfigToFilters(config, { name: data?.name })
    };

    return super.serialize({
      ...data,
      subpolicies: serializedPolicies,
      users: this.isNew ? this.users.serialize() : data.users,
      config: newConfig
    });
  }

  @computed
  get lastLogin() {
    return this.users.reduce((acc, model) => Math.max(acc, moment(model.get('last_login') || 0).valueOf()), 0);
  }

  @computed
  get numUsers() {
    return this.users.size;
  }

  addView = (view) => {
    const viewEntry = { id: `${view.id}` };

    if (view.type === 'Dashboard') {
      viewEntry.type = 'dashboard';
    }

    if (view.type === 'Saved View') {
      viewEntry.type = 'savedView';
    }

    if (!view.type) {
      return Promise.resolve();
    }

    const config = this.get('config');
    config.assets = config.assets || {};
    config.assets.reports = config.assets.reports || [];
    // Prevent dupes
    config.assets.reports = uniqWith([...config.assets.reports, viewEntry], isEqual);
    this.set('config', config);
    return this.save({}, { toast: false });
  };

  hasReport = (viewOption) => {
    const assignedReports = this.get('config.assets.reports') || [];
    return assignedReports.some((report) => isEqual(report, viewOption));
  };

  @computed
  get config() {
    return this.get('config') || {};
  }

  @computed
  get subpolicies() {
    return this.get('subpolicies') || [];
  }

  @computed
  get subpolicyToParentMap() {
    return Object.assign({}, ...this.subpolicies.map(({ id, parentId }) => ({ [`${id}`]: `${parentId}` })));
  }

  @computed
  get numViews() {
    const reportSet = new Set();
    (this.get('config.assets.reports') || []).forEach((report) => {
      if (report.type !== 'report') {
        reportSet.add(`${report.id}|${report.type.toLowerCase()}`);
      }
    });
    (this.get('template.config.assets.reports') || []).forEach((report) => {
      if (report.type !== 'report') {
        reportSet.add(`${report.id}|${report.type.toLowerCase()}`);
      }
    });
    return reportSet.size;
  }

  @computed
  get numAlertPolicies() {
    return this.subpolicies.length;
  }

  @computed
  get numMitigations() {
    let mitigationCount = 0;
    this.subpolicies.forEach((policy) => {
      if (!policy?.thresholds?.length) {
        return;
      }
      (policy.thresholds || []).forEach((threshold) => {
        mitigationCount += (threshold.mitigations || []).length;
      });
    });
    return mitigationCount;
  }

  @computed
  get subpolicyIDs() {
    return this.subpolicies.map((subpolicy) => subpolicy.id).filter(Boolean);
  }

  @computed
  get labels() {
    if (this.has('labels')) {
      return $labels.getLabelsById(this.get('labels').map((label) => label.id));
    }
    return $labels.getLabels('tenant', this.id);
  }
}
